import { fetchLeaderboard, type Board, type LeaderboardEntry, type LeaderboardMode } from '../server/laps';
import { getIdentity, type Identity } from '../server/identity';
import { isBackendConfigured } from '../server/supabase';
import { isOwnLeaderboardRow } from '../timing/GhostTarget';
import { formatTime, formatSectorTime } from '../timing/LapTimer';
import { attachMenuSounds } from './MenuSounds';
import { formatHandle } from '../server/handles';

export interface LeaderboardHandlers {
  onClose: () => void;
}

export class LeaderboardScreen {
  readonly root: HTMLDivElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly tabAlltime: HTMLButtonElement;
  private readonly tabWeekly: HTMLButtonElement;
  private readonly modeTop: HTMLButtonElement;
  private readonly modePersonalBest: HTMLButtonElement;
  private readonly trackId: string;
  private board: Board = 'alltime';
  private mode: LeaderboardMode = 'top';
  private refreshToken = 0;

  constructor(parent: HTMLElement, trackId: string, handlers: LeaderboardHandlers) {
    this.trackId = trackId;
    this.root = document.createElement('div');
    this.root.id = 'leaderboard-screen';
    this.root.className = 'tst-screen tst-overlay';
    this.root.style.zIndex = '35';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="tst-card tst-card-wide tst-card-tall tst-card-leaderboard">
        <div class="tst-title-sm">LEADERBOARD — Twin Suns Autodrome</div>
        <div class="tst-tabs">
          <button data-tab="alltime" class="tst-tab tst-tab-active" aria-pressed="true">All-time</button>
          <button data-tab="weekly" class="tst-tab" aria-pressed="false">This week</button>
        </div>
        <div class="tst-tabs tst-lb-filters">
          <button data-mode="top" class="tst-tab tst-tab-active" aria-pressed="true">Top 100</button>
          <button data-mode="personalBest" class="tst-tab" aria-pressed="false">Best per racer</button>
        </div>
        <div class="tst-lb-body"></div>
        <div class="tst-buttons" style="margin-top:12px;">
          <button data-action="close" class="tst-btn tst-btn-primary">Close</button>
        </div>
      </div>
    `;

    this.bodyEl = this.root.querySelector('.tst-lb-body') as HTMLDivElement;
    this.tabAlltime = this.root.querySelector('[data-tab="alltime"]') as HTMLButtonElement;
    this.tabWeekly = this.root.querySelector('[data-tab="weekly"]') as HTMLButtonElement;
    this.modeTop = this.root.querySelector('[data-mode="top"]') as HTMLButtonElement;
    this.modePersonalBest = this.root.querySelector('[data-mode="personalBest"]') as HTMLButtonElement;

    this.tabAlltime.addEventListener('click', () => this.setBoard('alltime'));
    this.tabWeekly.addEventListener('click', () => this.setBoard('weekly'));
    this.modeTop.addEventListener('click', () => this.setMode('top'));
    this.modePersonalBest.addEventListener('click', () => this.setMode('personalBest'));
    this.root.querySelector('[data-action="close"]')!.addEventListener('click', handlers.onClose);
    attachMenuSounds(this.root);
    parent.appendChild(this.root);
  }

  show(): void {
    this.root.hidden = false;
    void this.refresh();
  }
  hide(): void { this.root.hidden = true; }

  private setBoard(b: Board): void {
    if (this.board === b) return;
    this.board = b;
    this.tabAlltime.classList.toggle('tst-tab-active', b === 'alltime');
    this.tabWeekly.classList.toggle('tst-tab-active', b === 'weekly');
    this.tabAlltime.setAttribute('aria-pressed', String(b === 'alltime'));
    this.tabWeekly.setAttribute('aria-pressed', String(b === 'weekly'));
    void this.refresh();
  }

  private setMode(mode: LeaderboardMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.modeTop.classList.toggle('tst-tab-active', mode === 'top');
    this.modePersonalBest.classList.toggle('tst-tab-active', mode === 'personalBest');
    this.modeTop.setAttribute('aria-pressed', String(mode === 'top'));
    this.modePersonalBest.setAttribute('aria-pressed', String(mode === 'personalBest'));
    void this.refresh();
  }

  async refresh(): Promise<void> {
    if (!isBackendConfigured()) {
      this.bodyEl.innerHTML = `<div class="tst-empty">Backend not configured.<br/>Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local.</div>`;
      return;
    }
    const token = ++this.refreshToken;
    this.bodyEl.innerHTML = `<div class="tst-empty">Loading…</div>`;
    const rows = await fetchLeaderboard(this.board, this.trackId, 100, this.mode);
    if (token !== this.refreshToken) return;
    this.bodyEl.innerHTML = renderTable(rows, getIdentity());
  }
}

function renderTable(rows: LeaderboardEntry[], identity: Identity | null): string {
  if (rows.length === 0) {
    return `<div class="tst-empty">No times yet.<br/>Be the first.</div>`;
  }
  const head = `
    <div class="tst-lb-row tst-lb-head">
      <span class="tst-lb-rank">#</span>
      <span class="tst-lb-handle">Handle</span>
      <span class="tst-lb-date">Date</span>
      <span class="tst-lb-time">Total</span>
      <span class="tst-lb-sector">S1</span>
      <span class="tst-lb-sector">S2</span>
      <span class="tst-lb-sector">S3</span>
    </div>
  `;
  const body = rows
    .map((r) => {
      const mine = isOwnLeaderboardRow(r, identity) ? ' tst-lb-mine' : '';
      const date = formatLeaderboardDate(r.created_at);
      return `
        <div class="tst-lb-row${mine}">
          <span class="tst-lb-rank">${r.rank}</span>
          <span class="tst-lb-handle">
            <span>${escapeHtml(formatHandle(r.handle))}</span>
            ${r.homeworld ? `<span class="tst-lb-homeworld">${escapeHtml(r.homeworld)}</span>` : ''}
          </span>
          <span class="tst-lb-date">
            <time datetime="${escapeHtml(date.dateTime)}">
              <span>${date.day}</span>
              <span class="tst-lb-date-year">${date.year}</span>
            </time>
          </span>
          <span class="tst-lb-time">${formatTime(r.total_ms)}</span>
          <span class="tst-lb-sector">${formatSectorTime(r.sector1_ms)}</span>
          <span class="tst-lb-sector">${formatSectorTime(r.sector2_ms)}</span>
          <span class="tst-lb-sector">${formatSectorTime(r.sector3_ms)}</span>
        </div>
      `;
    })
    .join('');
  return head + body;
}

const LEADERBOARD_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatLeaderboardDate(createdAt: string): { day: string; year: string; dateTime: string } {
  const date = new Date(createdAt);
  if (!Number.isFinite(date.getTime())) {
    return { day: '--', year: '----', dateTime: '' };
  }
  return {
    day: `${LEADERBOARD_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`,
    year: String(date.getUTCFullYear()),
    dateTime: date.toISOString(),
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
